import { inject, Injectable } from '@angular/core';
import { AbstractRestService } from '../../../core/services/AbstractRestService';
import { Cache } from '../../../core/decorators/cache.decorator';
import { Observable, Subject } from 'rxjs';
import { Apps, StoreCacheKeys } from '../../../core/enums';
import { HttpParams } from '@angular/common/http';
import {
  Dictionary, Filter, IPagination, Search, SortingTypes,
  LanguageStoreService, ISnippet, ILanguage
} from '@cinetixx/cinetixx-ui';
import { HttpUtils } from '../../../core/utils';
import { tap } from 'rxjs/operators';
import { IRange } from '../../../core/interfaces';

@Injectable({
  providedIn: 'root'
})
export class SnippetsService extends AbstractRestService<ISnippet> {

  public getSnippetsFromRange$: Subject<ISnippet[]> = new Subject();

  protected readonly basePath = '/snippets';

  private readonly _languageStoreService = inject(LanguageStoreService);

  @Cache<ILanguage[]>(StoreCacheKeys.GetAllLanguages)
  public getAllLanguages(): Observable<ILanguage[]> {
    return this.apiService.get(`${ this.basePath }/languages/all`)
      .pipe(
        tap(res => this._languageStoreService.languages.set(res))
      );
  }

  @Cache<ISnippet[]>(StoreCacheKeys.GetAllSnippets)
  public getAllSnippets(language: string): Observable<ISnippet[]> {
    const params = new HttpParams({
      fromObject: {
        language: language,
        app: Apps.Admin
      }
    });

    return this.apiService.get(`${this.basePath}/all`, params);
  }

  public getPaginatedSnippetsAll(
    page: number,
    results: number,
    sortField: string,
    sortOrder: SortingTypes,
    search: Search = [],
    filter: Filter = [],
    dictionary: Dictionary = [],
    ...args
  ): Observable<IPagination<ISnippet>> {
    let params = HttpUtils.prepareQueryableParams(
      page,
      results,
      sortField,
      sortOrder,
      search,
      dictionary
    );
    const apps = ['manager', 'screener', 'entry', 'admin', 'cash'];

    if (args.some(x => !x.value)) {
      for (const appName of apps) {
        const appValue = args?.find(x => x.name === appName)?.value ?? true;

        if (appValue) {
          switch (appName) {
            case 'manager':
              params = params.append('Filter', 'appId:2');
              break;
            case 'screener':
              params = params.append('Filter', 'appId:4');
              break;
            case 'entry':
              params = params.append('Filter', 'appId:5');
              break;
            case 'admin':
              params = params.append('Filter', 'appId:1');
              break;
            case 'cash':
              params = params.append('Filter', 'appId:10');
              break;
            default:
              break;
          }
        }
      }
    }

    return this.apiService.get(`${this.basePath}${filter[0] ? ('?LanguageId=' + filter[0]?.value[0]) : ''}`, params);
  }

  public getSnippetsFromRange(range: IRange, employeeId: number): Observable<ISnippet[]> {
    const params = new HttpParams({
      fromObject: {
        ...range,
        employeeId
      }
    });

    return this.apiService.get(`${this.basePath}/from-range`, params).pipe(
      tap((res) => this.getSnippetsFromRange$.next(res))
    );
  }

  public addSnippet(namespace: string, value: string): Observable<ISnippet> {
    const body = {
      namespace,
      value,
      snippetLanguageId: this._languageStoreService.language.id,
      appId: Apps.Admin
    };

    return this.apiService.post(`${this.basePath}`, body);
  }

  public importSnippets(snippets: ISnippet[]) {
    return this.apiService.post(`${this.basePath}/import`, { snippets });
  }
}
